import { useState } from "react";

import { makePostApiCall, makePutApiCall } from "../../../shared/makeApiCall";
import CategoryDetailsForm from "../../../components/forms/setup/categories/CategoryDetailsForm";
import { BannerNotificationType } from "../../../components/SimpleNotification";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import {
  generateUniqueNumber,
  updateObjectInArray,
} from "../../../shared/utility";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";

export function CategoriesViewModel(logout, addSnackbar) {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState({
    data: [],
  });
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const showErrorNotification = (callResult, fallbackTitle) => {
    setShowSimpleNotification({
      show: true,
      title:
        callResult?.data?.message || callResult?.data?.title || fallbackTitle,
      type: BannerNotificationType.Error,
    });
  };

  const getCategories = async function (search, page) {
    setLoading(true);

    let callResult = await makePostApiCall(
      `categories?page=${page}&search=${search}`,
    );
    setLoading(false);
    if (callResult.success) {
      setCategories(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorNotification(callResult, "Could not fetch categories");
    }
  };

  const createCategory = async function (values) {
    const payload = { ...values };
    const needsMultipart =
      Array.isArray(values.categoryImage) &&
      values.categoryImage[0] instanceof File;
    let callResult = needsMultipart
      ? await makePostApiCall(
          "categories/create",
          payload,
          "multipart/form-data",
        )
      : await makePostApiCall("categories/create", payload);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult?.data?.message || "Category added",
          SnackbarType.Success,
        ),
      );
      setSlideOver({
        childComponent: null,
        open: false,
      });
      setTimeout(function () {
        getCategories("", 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorNotification(callResult, "Could not create category");
    }
  };

  const updateCategory = async function (values, index) {
    const needsMultipart =
      Array.isArray(values.categoryImage) &&
      values.categoryImage[0] instanceof File;
    let callResult = needsMultipart
      ? await makePutApiCall(
          `categories/${values.id}`,
          values,
          "multipart/form-data",
        )
      : await makePutApiCall(`categories/${values.id}`, values);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult?.data?.message || "Category updated",
          SnackbarType.Success,
        ),
      );
      setSlideOver({
        childComponent: null,
        open: false,
      });

      const currentItems = [...categories.data];
      var payload = {
        index: index,
        item: callResult.data.item,
      };
      const newItems = updateObjectInArray(currentItems, payload);
      setCategories({ ...categories, data: newItems });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorNotification(callResult, "Could not update category");
    }
  };

  const openAddCategorySlideOver = (isEdit, category, index) => {
    setSlideOver({
      childComponent: (
        <CategoryDetailsForm
          category={category}
          isEdit={isEdit}
          setOpen={setSlideOver}
          actionCall={(payload) =>
            isEdit ? updateCategory(payload, index) : createCategory(payload)
          }
          title={isEdit ? `Update ${category.name}` : "Add new category"}
        />
      ),
      open: true,
    });
  };

  const deleteCategory = async function (id, index) {
    setLoading(true);

    let callResult = await makePostApiCall(`deleteCategory/${id}`);
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const newItems = categories.data.filter((item) => item.id !== id);
      setCategories({ ...categories, data: newItems });

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorNotification(callResult, "Could not delete category");
    }
  };

  const askDeleteCategory = function (category, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteCategory(category.id, index)}
          message={`Are you sure you want to delete  ${category.name}?`}
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  return {
    openAddCategorySlideOver,
    getCategories,
    loading,
    categories,
    setSlideOver,
    slideOver,
    setShowSimpleNotification,
    showSimpleNotification,
    setEmptyModelOpen,
    emptyModal,
    askDeleteCategory,
  };
}
